import React, { useState } from "react";
import { X, Check } from "lucide-react";

export default function TermsModal({ isOpen, onClose, onAccept, readOnly = false }) {
  const [accepted, setAccepted] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (readOnly) {
      if (onClose) onClose();
      return;
    }
    if (accepted && onAccept) {
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
      <div 
        className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden transform scale-in transition-all"
        data-testid="terms-modal"
      >
        {/* Header */}
        <header className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#00D285]/10 flex items-center justify-center text-[#00D285]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="font-display font-bold text-slate-900 dark:text-white text-base">
              Termos de Uso
            </h2>
          </div>
          {readOnly && (
            <button 
              onClick={onClose} 
              className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="text-center pb-2">
            <h3 className="font-display font-bold text-slate-800 dark:text-slate-100 text-sm">
              CATÁLOGO DE ASSISTÊNCIA AUTOMOTIVA DE EMERGÊNCIA
            </h3>
          </div>
          
          <div className="space-y-3 text-slate-600 dark:text-slate-300 text-xs leading-relaxed max-h-[45vh] overflow-y-auto border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl">
            <p>
              A GoProtege disponibiliza em seu aplicativo um Catálogo de Assistência Automotiva de Emergência, com o objetivo de facilitar a localização de prestadores de serviços automotivos próximos à localização do usuário.
            </p>
            <p className="font-semibold text-slate-800 dark:text-slate-200">
              Ao utilizar esta funcionalidade, o usuário declara estar ciente e concorda que:
            </p>
            <ul className="list-disc pl-4 space-y-2">
              <li>O catálogo possui caráter exclusivamente informativo e facilitador de contato.</li>
              <li>A GoProtege não presta diretamente os serviços exibidos no catálogo.</li>
              <li>Os prestadores listados são empresas e profissionais independentes.</li>
              <li>A GoProtege não garante disponibilidade, tempo de atendimento, qualidade, preços ou execução dos serviços.</li>
              <li>A contratação dos serviços ocorre diretamente entre o usuário e o prestador escolhido.</li>
              <li>Todos os custos, pagamentos, negociações, garantias e responsabilidades decorrentes da contratação são exclusivamente do usuário e do prestador contratado.</li>
              <li>Os serviços apresentados no catálogo não fazem parte dos serviços de rastreamento, monitoramento ou quaisquer planos contratados junto à GoProtege.</li>
              <li>A GoProtege não poderá ser responsabilizada por atrasos, falhas, cancelamentos, danos materiais, danos morais, prejuízos financeiros ou quaisquer problemas decorrentes dos serviços prestados por terceiros.</li>
            </ul>
            <p className="font-medium pt-2 border-t border-slate-200/50 dark:border-slate-800/50">
              Ao prosseguir, o usuário declara ter lido e aceitado integralmente os presentes termos.
            </p>
          </div>

          {/* Accept controls */}
          {!readOnly ? (
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input 
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="sr-only"
                />
                <div className={`mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${
                  accepted 
                    ? "bg-[#00D285] border-[#00D285] text-white" 
                    : "border-slate-200 dark:border-slate-700 group-hover:border-[#00D285] bg-white dark:bg-slate-900"
                }`}>
                  {accepted && <Check size={14} strokeWidth={3} />}
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 leading-normal select-none">
                  Declaro estar ciente de que o Catálogo de Assistência Automotiva de Emergência da GoProtege consiste apenas em uma ferramenta para localização de prestadores independentes. Os serviços contratados, seus custos e responsabilidades não possuem vínculo com os serviços de rastreamento contratados junto à GoProtege.
                </span>
              </label>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-xl text-xs font-semibold">
              <div className="h-4 w-4 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                <Check size={10} strokeWidth={3} />
              </div>
              Você leu e aceitou este termo de uso.
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3 bg-slate-50/50 dark:bg-slate-900/50">
          {readOnly ? (
            <button 
              onClick={onClose}
              className="btn-press px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold text-sm transition-colors"
            >
              Fechar
            </button>
          ) : (
            <button 
              disabled={!accepted}
              onClick={handleAccept}
              className={`btn-press px-6 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                accepted 
                  ? "bg-[#00D285] hover:bg-[#00B874] text-white shadow-sm" 
                  : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed"
              }`}
            >
              Aceitar e Prosseguir
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
